/**
 * Progress tracking service for API communication
 * Handles topic completion, video progress tracking, and resume functionality
 */
import axios, { AxiosInstance } from 'axios'
import { getConfig } from '../config/Constants'
import type { ApiResponse } from '../types/api.types'
import type {
    TopicCompleteRequest,
    VideoProgressUpdateRequest,
    TopicCompletionResult,
    TopicProgressResponse,
    CourseProgressDetailResponse,
    ResumePositionResponse,
    CompletedTopicsResponse
} from '../types/progress.types'

let apiInstance: AxiosInstance | null = null

function getApiInstance() {
    if (!apiInstance) {
        const config = getConfig()
        apiInstance = axios.create({
            baseURL: `${config.apiUrl}`,
            headers: {
                'Content-Type': 'application/json',
            },
        })

        // Add request interceptor to add auth token
        apiInstance.interceptors.request.use(
            (config) => {
                const token = localStorage.getItem('access_token')
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`
                }
                return config
            },
            (error) => {
                return Promise.reject(error)
            }
        )
    }
    return apiInstance
}

// ============================================================================
// Service Functions
// ============================================================================

export const progressService = {
    markTopicComplete,
    updateVideoProgress,
    getCourseProgressDetail,
    getResumePosition,
    getCompletedTopicIds,
}

/**
 * Mark a topic as completed after watching its video
 * This updates the topic progress and recalculates overall course progress
 */
async function markTopicComplete(
    request: TopicCompleteRequest
): Promise<ApiResponse<TopicCompletionResult>> {
    try {
        const response = await getApiInstance().post('/progress/topic/complete', request)
        const data = response.data

        // Handle wrapped response from backend
        let result: TopicCompletionResult
        if (data.success && data.data) {
            result = data.data
        } else if (data.data) {
            result = data.data
        } else {
            result = data
        }

        return {
            success: true,
            data: result
        }
    } catch (error: any) {
        console.error('Error marking topic complete:', error)
        return {
            success: false,
            error: error.response?.data?.detail || error.message || 'Failed to mark topic as complete'
        }
    }
}

/**
 * Update video watch progress without marking complete
 * Used for tracking resume position
 */
async function updateVideoProgress(
    request: VideoProgressUpdateRequest
): Promise<ApiResponse<TopicProgressResponse>> {
    try {
        const response = await getApiInstance().put('/progress/topic/video-progress', request)
        const data = response.data

        let result: TopicProgressResponse
        if (data.success && data.data) {
            result = data.data
        } else if (data.data) {
            result = data.data
        } else {
            result = data
        }

        return {
            success: true,
            data: result
        }
    } catch (error: any) {
        console.error('Error updating video progress:', error)
        return {
            success: false,
            error: error.response?.data?.detail || error.message || 'Failed to update video progress'
        }
    }
}

/**
 * Get detailed progress information for a course
 * Includes completion status, per-module breakdown, and resume position
 */
async function getCourseProgressDetail(
    enrollmentId: string
): Promise<ApiResponse<CourseProgressDetailResponse>> {
    try {
        const response = await getApiInstance().get(`/progress/${enrollmentId}/detail`)
        const data = response.data

        let result: CourseProgressDetailResponse
        if (data.success && data.data) {
            result = data.data
        } else if (data.data) {
            result = data.data
        } else {
            result = data
        }

        return {
            success: true,
            data: result
        }
    } catch (error: any) {
        console.error('Error getting course progress detail:', error)
        return {
            success: false,
            error: error.response?.data?.detail || error.message || 'Failed to get course progress'
        }
    }
}

/**
 * Get the resume position for a course (continue where you left off)
 */
async function getResumePosition(
    enrollmentId: string
): Promise<ApiResponse<ResumePositionResponse>> {
    try {
        const response = await getApiInstance().get(`/progress/${enrollmentId}/resume`)
        const data = response.data

        let result: ResumePositionResponse
        if (data.success && data.data) {
            result = data.data
        } else if (data.data) {
            result = data.data
        } else {
            result = data
        }

        return {
            success: true,
            data: result
        }
    } catch (error: any) {
        console.error('Error getting resume position:', error)
        return {
            success: false,
            error: error.response?.data?.detail || error.message || 'Failed to get resume position'
        }
    }
}

/**
 * Get list of completed topic IDs for sidebar checkmarks
 */
async function getCompletedTopicIds(
    enrollmentId: string
): Promise<ApiResponse<CompletedTopicsResponse>> {
    try {
        const response = await getApiInstance().get(`/progress/${enrollmentId}/completed-topics`)
        const data = response.data

        let result: CompletedTopicsResponse
        if (data.success && data.data) {
            result = data.data
        } else if (data.data) {
            result = data.data
        } else {
            result = data
        }

        return {
            success: true,
            data: result
        }
    } catch (error: any) {
        console.error('Error getting completed topics:', error)
        return {
            success: false,
            error: error.response?.data?.detail || error.message || 'Failed to get completed topics'
        }
    }
}

export default progressService
